import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api, API, apiErrorMessage } from "@/lib/api";
import { useProperty } from "@/contexts/PropertyContext";
import { PageHeader } from "@/components/PageHelpers";
import { BudgetsUploadCard } from "@/components/BudgetsUploadCard";
import { BoardActivityCard } from "@/components/BoardActivityCard";
import { ReserveForecastCard } from "@/components/ReserveForecastCard";
import { ExpensesCard } from "@/components/ExpensesCard";
import { TicketSpendCard } from "@/components/TicketSpendCard";
import { ExpenseAnomaliesCard } from "@/components/ExpenseAnomaliesCard";
import { BoardPollsCard } from "@/components/BoardPollsCard";
import { BoardPacketDialog } from "@/components/BoardPacketDialog";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import {
  ResponsiveContainer, ComposedChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid, Legend,
  Area, PieChart, Pie, Cell, Line, BarChart,
} from "recharts";
import {
  TrendingUp, TrendingDown, DollarSign, Users, AlertTriangle, RefreshCw,
  Download, Clock, ArrowUpRight, Mail, Target,
} from "lucide-react";
import { toast } from "sonner";

const money = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const moneyShort = (n) => `$${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}`;
const compact = (n) => new Intl.NumberFormat(undefined, { notation: "compact", maximumFractionDigits: 1 }).format(n || 0);

const PIE_COLORS = ["#f97316", "#4f46e5", "#10b981", "#eab308", "#ec4899", "#22d3ee", "#a78bfa", "#f43f5e"];
const AGING_COLORS = { "Current": "#10b981", "1-30": "#eab308", "31-60": "#f97316", "61-90": "#ec4899", "90+": "#ef4444" };

function Kpi({ label, value, sub, tone = "default", icon: Icon, testId }) {
  const toneClasses = {
    default: "border-white/10 bg-card",
    good: "border-emerald-500/20 bg-emerald-500/[0.03]",
    warn: "border-amber-500/20 bg-amber-500/[0.03]",
    bad: "border-red-500/20 bg-red-500/[0.03]",
  }[tone];
  return (
    <Card className={"p-4 " + toneClasses} data-testid={testId}>
      <div className="flex items-center justify-between mb-2">
        <div className="text-[10px] uppercase tracking-widest text-zinc-400">{label}</div>
        {Icon && <Icon className="h-3.5 w-3.5 text-zinc-500" />}
      </div>
      <div className="text-2xl font-semibold font-display text-white tabular-nums">{value}</div>
      {sub && <div className="text-[11px] text-zinc-500 mt-1">{sub}</div>}
    </Card>
  );
}

function TooltipBox({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="rounded-md border border-white/10 bg-[#0a0a0b]/95 backdrop-blur px-3 py-2 text-xs shadow-lg">
      <div className="text-zinc-300 mb-1">{label}</div>
      {payload.filter((p) => p.value != null).map((p, i) => (
        <div key={i} className="flex items-center gap-2">
          <span className="h-2 w-2 rounded-full" style={{ background: p.color }} />
          <span className="text-zinc-400">{p.name}:</span>
          <span className="text-white tabular-nums">{money(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function FinancePage() {
  const { properties, propertyId } = useProperty();
  const [scope, setScope] = useState(propertyId || "all");
  const [months, setMonths] = useState(12);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [packetOpen, setPacketOpen] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/finance/summary", { params: { property_id: scope, months } });
      setData(data);
    } catch (e) {
      toast.error(apiErrorMessage(e) || "Failed to load finance data");
    } finally {
      setLoading(false);
    }
  }, [scope, months]);

  useEffect(() => { load(); }, [load]);

  const exportCsv = () => {
    const url = `${API}/admin/finance/summary.csv?property_id=${encodeURIComponent(scope)}&months=${months}`;
    // Cookie auth (`credentials: 'include'`) — no bearer token exposed to JS.
    fetch(url, {
      credentials: "include",
    }).then((r) => r.blob()).then((blob) => {
      const a = document.createElement("a");
      const dl = URL.createObjectURL(blob);
      a.href = dl; a.download = `finance-summary-${scope}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(dl);
    }).catch(() => toast.error("Export failed"));
  };

  const scopeLabel = useMemo(() => {
    if (scope === "all") return "Portfolio (all communities)";
    return properties.find((p) => p.id === scope)?.name || scope;
  }, [scope, properties]);

  const kpis = data?.kpis || {};
  const budget = data?.budget || null;
  const momPositive = (kpis.mom_delta || 0) >= 0;
  const arCritical = (kpis.ar_total || 0) > 25000;

  // Budget delta helpers
  const budgetTone = budget ? (budget.on_track ? "good" : "bad") : "default";
  const budgetPill = budget ? (
    <Badge
      variant="outline"
      className={
        "ml-2 tabular-nums " +
        (budget.on_track
          ? "border-emerald-500/40 text-emerald-300 bg-emerald-500/10"
          : "border-red-500/40 text-red-300 bg-red-500/10")
      }
      data-testid="budget-pill"
    >
      {moneyShort(Math.abs(budget.variance))} {budget.on_track ? "under budget" : "over budget"} · {budget.variance_pct}%
    </Badge>
  ) : null;

  return (
    <div>
      <PageHeader
        eyebrow="Finance"
        title="Financial Command Center"
        subtitle={
          <span>
            {scopeLabel}
            {data?.as_of && <span className="text-zinc-500"> · as of {new Date(data.as_of).toLocaleString()}</span>}
          </span>
        }
      />

      {/* Toolbar — scope + range + refresh + export + email packet */}
      <div className="flex flex-wrap items-center gap-2 mb-4" data-testid="finance-toolbar">
        <Select value={scope} onValueChange={setScope}>
          <SelectTrigger data-testid="finance-scope" className="w-56 bg-white/[0.04] border-white/10 text-white h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="all" data-testid="finance-scope-all">All communities</SelectItem>
            {properties.map((p) => (
              <SelectItem key={p.id} value={p.id} data-testid={`finance-scope-${p.id}`}>{p.name}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <Select value={String(months)} onValueChange={(v) => setMonths(Number(v))}>
          <SelectTrigger data-testid="finance-range" className="w-32 bg-white/[0.04] border-white/10 text-white h-9">
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-[#0a0a0b] border-white/10 text-white">
            <SelectItem value="3">Last 3 mo</SelectItem>
            <SelectItem value="6">Last 6 mo</SelectItem>
            <SelectItem value="12">Last 12 mo</SelectItem>
            <SelectItem value="24">Last 24 mo</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={load} disabled={loading} className="border-white/10 h-9" data-testid="finance-refresh">
          <RefreshCw className={"h-3.5 w-3.5 mr-1 " + (loading ? "animate-spin" : "")} /> Refresh
        </Button>
        <div className="ml-auto flex flex-wrap gap-2">
          <Button
            onClick={() => setPacketOpen(true)}
            className="bg-indigo-500 hover:bg-indigo-600 rounded-full h-9"
            data-testid="finance-email-packet"
          >
            <Mail className="h-3.5 w-3.5 mr-1" /> Email board packet
          </Button>
          <Button onClick={exportCsv} className="bg-orange-500 hover:bg-orange-600 rounded-full h-9" data-testid="finance-export-csv">
            <Download className="h-3.5 w-3.5 mr-1" /> Export CSV
          </Button>
        </div>
      </div>

      {/* KPI ROW */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-5 gap-3 mb-4">
        <Kpi
          testId="kpi-ytd"
          label="Collected YTD"
          value={
            <span className="inline-flex items-center flex-wrap gap-1">
              {money(kpis.collected_ytd)}
              {budgetPill}
            </span>
          }
          sub={
            budget
              ? `Budget YTD ${moneyShort(budget.ytd_budget)} · Annual ${moneyShort(budget.annual_budget)}`
              : `${data?.scope?.transaction_count ?? 0} transactions`
          }
          icon={budget ? Target : DollarSign}
          tone={budgetTone}
        />
        <Kpi
          testId="kpi-mtd"
          label="This month"
          value={money(kpis.collected_this_month)}
          sub={
            <span className={momPositive ? "text-emerald-400" : "text-red-400"}>
              {momPositive ? <TrendingUp className="h-3 w-3 inline mr-1" /> : <TrendingDown className="h-3 w-3 inline mr-1" />}
              {money(kpis.mom_delta)} vs last mo
            </span>
          }
          tone={momPositive ? "good" : "warn"}
        />
        <Kpi
          testId="kpi-ar"
          label="Accounts receivable"
          value={money(kpis.ar_total)}
          sub={`${kpis.delinquent_count ?? 0} delinquent · ${kpis.resident_count ?? 0} residents`}
          tone={arCritical ? "bad" : "warn"}
          icon={AlertTriangle}
        />
        <Kpi
          testId="kpi-pending"
          label="Pending"
          value={money(kpis.pending)}
          sub="Not yet cleared"
          icon={Clock}
        />
        <Kpi
          testId="kpi-vel"
          label="Avg days to pay"
          value={kpis.avg_days_to_pay != null ? `${kpis.avg_days_to_pay}d` : "—"}
          sub="Charge → payment"
          icon={Users}
        />
      </div>

      {/* CASHFLOW + AR AGING */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <Card className="lg:col-span-2 p-4 border-white/10 bg-card" data-testid="finance-cashflow">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Cash flow</div>
              <div className="text-base font-semibold font-display mt-0.5">
                Monthly collected · refunded · 3-mo forecast
              </div>
            </div>
            <div className="text-[11px] text-zinc-500">Last {months} months + forecast</div>
          </div>
          <div style={{ width: "100%", height: 260, minHeight: 260 }}>
            {data && (
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <ComposedChart data={data.monthly_cashflow} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                  <defs>
                    <linearGradient id="collectedG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#f97316" stopOpacity={0.6} />
                      <stop offset="100%" stopColor="#f97316" stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id="refundedG" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#ef4444" stopOpacity={0.4} />
                      <stop offset="100%" stopColor="#ef4444" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="label" stroke="#71717a" tick={{ fontSize: 11 }} />
                  <YAxis stroke="#71717a" tick={{ fontSize: 11 }} tickFormatter={compact} />
                  <Tooltip content={<TooltipBox />} />
                  <Legend wrapperStyle={{ fontSize: 12, color: "#a1a1aa" }} />
                  <Area type="monotone" dataKey="collected" stroke="#f97316" fill="url(#collectedG)" strokeWidth={2} name="Collected" connectNulls={false} />
                  <Area type="monotone" dataKey="refunded" stroke="#ef4444" fill="url(#refundedG)" strokeWidth={2} name="Refunded" connectNulls={false} />
                  <Line
                    type="monotone" dataKey="forecast" stroke="#a78bfa" strokeWidth={2}
                    strokeDasharray="6 4" dot={{ r: 3, fill: "#a78bfa" }} name="Forecast"
                    connectNulls
                  />
                </ComposedChart>
              </ResponsiveContainer>
            )}
          </div>
          {budget && (
            <div className="mt-2 flex items-center gap-2 text-[11px] text-zinc-500" data-testid="budget-context">
              <Target className="h-3 w-3 text-orange-400" />
              <span>
                Budget vs actual (FY {budget.fiscal_year}): {moneyShort(kpis.collected_ytd)} of {moneyShort(budget.ytd_budget)} YTD budget
                {" · "}
                <span className={budget.on_track ? "text-emerald-300" : "text-red-300"}>
                  {budget.on_track ? "on track" : "behind"} by {moneyShort(Math.abs(budget.variance))}
                </span>
              </span>
            </div>
          )}
        </Card>

        <Card className="p-4 border-white/10 bg-card" data-testid="finance-aging">
          <div className="mb-3">
            <div className="overline">AR aging</div>
            <div className="text-base font-semibold font-display mt-0.5">Delinquency by bucket</div>
          </div>
          <div style={{ width: "100%", height: 260, minHeight: 260 }}>
            {data && (
              <ResponsiveContainer width="100%" height="100%" minHeight={260}>
                <BarChart data={data.ar_aging} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#ffffff10" />
                  <XAxis dataKey="bucket" stroke="#71717a" tick={{ fontSize: 10 }} />
                  <YAxis stroke="#71717a" tick={{ fontSize: 10 }} tickFormatter={compact} />
                  <Tooltip content={<TooltipBox />} />
                  <Bar dataKey="amount" name="Owed" radius={[4, 4, 0, 0]}>
                    {data.ar_aging.map((entry, idx) => (
                      <Cell key={idx} fill={AGING_COLORS[entry.bucket] || "#f97316"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="mt-2 grid grid-cols-5 gap-1 text-[10px]">
            {(data?.ar_aging || []).map((a) => (
              <div key={a.bucket} className="text-center">
                <div className="text-zinc-500 truncate">{a.bucket}</div>
                <div className="text-zinc-300 tabular-nums">{a.count}</div>
              </div>
            ))}
          </div>
        </Card>
      </div>

      {/* REVENUE MIX + TOP DELINQUENTS */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 mb-4">
        <Card className="p-4 border-white/10 bg-card" data-testid="finance-revenue-mix">
          <div className="mb-3">
            <div className="overline">Revenue mix</div>
            <div className="text-base font-semibold font-display mt-0.5">Where your money comes from</div>
          </div>
          <div style={{ width: "100%", height: 220 }}>
            {data && data.revenue_mix.length > 0 && (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={data.revenue_mix} dataKey="amount" nameKey="label" cx="50%" cy="50%" outerRadius={80} innerRadius={45}
                    stroke="#0a0a0b" strokeWidth={2}
                  >
                    {data.revenue_mix.map((_, idx) => (
                      <Cell key={idx} fill={PIE_COLORS[idx % PIE_COLORS.length]} />
                    ))}
                  </Pie>
                  <Tooltip content={<TooltipBox />} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
          <div className="mt-2 space-y-1">
            {(data?.revenue_mix || []).slice(0, 6).map((r, i) => (
              <div key={r.type} className="flex items-center justify-between text-xs">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2 w-2 rounded-full shrink-0" style={{ background: PIE_COLORS[i % PIE_COLORS.length] }} />
                  <span className="text-zinc-300 truncate">{r.label}</span>
                </div>
                <span className="tabular-nums text-white">{money(r.amount)}</span>
              </div>
            ))}
          </div>
        </Card>

        <Card className="lg:col-span-2 p-4 border-white/10 bg-card" data-testid="finance-top-delinquents">
          <div className="flex items-center justify-between mb-3">
            <div>
              <div className="overline">Delinquency</div>
              <div className="text-base font-semibold font-display mt-0.5">Top 15 delinquent balances</div>
            </div>
            <Link to="/admin/delinquency" className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1" data-testid="finance-open-delinquency">
              Open pipeline <ArrowUpRight className="h-3 w-3" />
            </Link>
          </div>
          <div className="overflow-hidden rounded-md border border-white/5">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03]">
                <tr>
                  <th className="text-left px-3 py-2 text-zinc-400">Resident</th>
                  <th className="text-left px-3 py-2 text-zinc-400">Unit</th>
                  <th className="text-right px-3 py-2 text-zinc-400">Owed</th>
                  <th className="text-right px-3 py-2 text-zinc-400">Aging</th>
                </tr>
              </thead>
              <tbody>
                {(data?.top_delinquents || []).map((d) => (
                  <tr key={d.resident_id} className="border-t border-white/5" data-testid={`delinquent-row-${d.resident_id}`}>
                    <td className="px-3 py-1.5">
                      <Link to={`/admin/residents/${d.resident_id}`} className="text-zinc-200 hover:text-orange-300 truncate">
                        {d.name}
                      </Link>
                    </td>
                    <td className="px-3 py-1.5 text-zinc-400">{d.unit || "—"}</td>
                    <td className="px-3 py-1.5 text-right text-red-300 tabular-nums font-medium">{money(Math.abs(d.balance))}</td>
                    <td className="px-3 py-1.5 text-right">
                      <Badge variant="outline" className={
                        d.days_past_due > 90 ? "border-red-500/40 text-red-300" :
                        d.days_past_due > 60 ? "border-pink-500/40 text-pink-300" :
                        d.days_past_due > 30 ? "border-amber-500/40 text-amber-300" :
                        d.days_past_due > 0 ? "border-yellow-500/40 text-yellow-300" :
                        "border-emerald-500/40 text-emerald-300"
                      }>
                        {d.days_past_due}d
                      </Badge>
                    </td>
                  </tr>
                ))}
                {data && data.top_delinquents.length === 0 && (
                  <tr><td colSpan={4} className="px-3 py-6 text-center text-emerald-300 text-xs">Everyone is current.</td></tr>
                )}
              </tbody>
            </table>
          </div>
        </Card>
      </div>

      {/* BUDGETS + BOARD ACTIVITY (side by side) */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <BudgetsUploadCard compact defaultPropertyId={scope} onUploaded={load} />
        <BoardActivityCard compact limit={20} />
      </div>

      {/* RESERVE FUND FORECAST */}
      <div className="mb-4">
        <ReserveForecastCard scope={scope} />
      </div>

      {/* EXPENSE LEDGER + ANOMALY WATCH */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-4">
        <ExpensesCard compact defaultPropertyId={scope} />
        <TicketSpendCard defaultPropertyId={scope} />
        <ExpenseAnomaliesCard scope={scope} />
      </div>

      {/* BOARD VOTING */}
      <div className="mb-4">
        <BoardPollsCard scope={scope} />
      </div>

      {/* PORTFOLIO BREAKDOWN (only when scope=all) */}
      {scope === "all" && data && data.portfolio_breakdown?.length > 0 && (
        <Card className="p-4 border-white/10 bg-card mb-4" data-testid="finance-portfolio">
          <div className="mb-3">
            <div className="overline">Portfolio</div>
            <div className="text-base font-semibold font-display mt-0.5">Per-community performance</div>
          </div>
          <div className="overflow-hidden rounded-md border border-white/5">
            <table className="w-full text-xs">
              <thead className="bg-white/[0.03]">
                <tr>
                  <th className="text-left px-3 py-2 text-zinc-400">Community</th>
                  <th className="text-right px-3 py-2 text-zinc-400">Units</th>
                  <th className="text-right px-3 py-2 text-zinc-400">Collected YTD</th>
                  <th className="text-right px-3 py-2 text-zinc-400">AR total</th>
                  <th className="text-right px-3 py-2 text-zinc-400">Health</th>
                </tr>
              </thead>
              <tbody>
                {data.portfolio_breakdown.map((p) => {
                  const ratio = p.collected_ytd > 0 ? p.ar_total / p.collected_ytd : 1;
                  const healthy = ratio < 0.1;
                  return (
                    <tr key={p.property_id} className="border-t border-white/5" data-testid={`portfolio-row-${p.property_id}`}>
                      <td className="px-3 py-2">
                        <button
                          onClick={() => setScope(p.property_id)}
                          className="text-zinc-200 hover:text-orange-300"
                          data-testid={`portfolio-drill-${p.property_id}`}
                        >
                          {p.name}
                        </button>
                      </td>
                      <td className="px-3 py-2 text-right text-zinc-400 tabular-nums">{p.units || "—"}</td>
                      <td className="px-3 py-2 text-right text-emerald-300 tabular-nums">{money(p.collected_ytd)}</td>
                      <td className="px-3 py-2 text-right text-red-300 tabular-nums">{money(p.ar_total)}</td>
                      <td className="px-3 py-2 text-right">
                        <Badge variant="outline" className={healthy ? "border-emerald-500/40 text-emerald-300" : "border-amber-500/40 text-amber-300"}>
                          {healthy ? "Healthy" : "Watch"}
                        </Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* RECENT TRANSACTIONS */}
      <Card className="p-4 border-white/10 bg-card" data-testid="finance-recent-tx">
        <div className="flex items-center justify-between mb-3">
          <div>
            <div className="overline">Ledger</div>
            <div className="text-base font-semibold font-display mt-0.5">Latest 20 transactions</div>
          </div>
          <Link to="/admin/payments" className="text-xs text-orange-300 hover:text-orange-200 inline-flex items-center gap-1" data-testid="finance-open-payments">
            Full ledger <ArrowUpRight className="h-3 w-3" />
          </Link>
        </div>
        <div className="overflow-hidden rounded-md border border-white/5">
          <table className="w-full text-xs">
            <thead className="bg-white/[0.03]">
              <tr>
                <th className="text-left px-3 py-2 text-zinc-400">When</th>
                <th className="text-left px-3 py-2 text-zinc-400">Resident</th>
                <th className="text-left px-3 py-2 text-zinc-400">Type</th>
                <th className="text-right px-3 py-2 text-zinc-400">Amount</th>
                <th className="text-right px-3 py-2 text-zinc-400">Status</th>
              </tr>
            </thead>
            <tbody>
              {(data?.recent_transactions || []).map((t) => (
                <tr key={t.session_id} className="border-t border-white/5">
                  <td className="px-3 py-1.5 text-zinc-500 tabular-nums">{(t.created_at || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-zinc-300 truncate max-w-[220px]">{t.user_name || t.user_email}</td>
                  <td className="px-3 py-1.5 text-zinc-400">{(t.payment_type || "").replace(/_/g, " ")}</td>
                  <td className="px-3 py-1.5 text-right text-white tabular-nums">{money(t.amount)}</td>
                  <td className="px-3 py-1.5 text-right">
                    <Badge variant="outline" className={
                      t.payment_status === "paid" ? "border-emerald-500/40 text-emerald-300" :
                      t.payment_status === "pending" ? "border-amber-500/40 text-amber-300" :
                      t.payment_status === "refunded" ? "border-red-500/40 text-red-300" :
                      "border-white/10 text-zinc-400"
                    }>{t.payment_status}</Badge>
                  </td>
                </tr>
              ))}
              {data && data.recent_transactions.length === 0 && (
                <tr><td colSpan={5} className="px-3 py-6 text-center text-zinc-500 text-xs">No transactions in this window.</td></tr>
              )}
            </tbody>
          </table>
        </div>
      </Card>

      <BoardPacketDialog
        open={packetOpen}
        onOpenChange={setPacketOpen}
        scope={scope}
        scopeLabel={scopeLabel}
        months={months}
      />
    </div>
  );
}
